<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import { cloneDeep } from 'lodash';
import mainViewTabMixin from '../../mixins/mainViewTab';
import paginationMixin from '../../mixins/pagination';
import { userListHeaders } from './config';
import UserListItem from './components/UserListItem';
import NewUserDialog from './components/NewUserDialog';
import RbxConfirmDialog from '../../shared/reybex/RbxConfirmDialog';

export default {
  name: 'UserList',
  components: {
    UserListItem,
    NewUserDialog,
    RbxConfirmDialog,
  },
  mixins: [mainViewTabMixin, paginationMixin],
  data() {
    return {
      isInitialized: false,
      headers: userListHeaders,
      showNewUserDialog: false,
      isConfirmDialogDisplayed: false,
      confirmDeletionMessage: '',
      userToDelete: {},
      userItems: [],
      isDeleteFilter: false,
      isDeleteOptions: [{ id: null, text: 'alle' }, { id: true, text: 'ja' }, { id: false, text: 'nein' }],
      searchTerm: '',
    };
  },
  computed: {
    ...mapState(['userList']),
    ...mapState('userList', ['meta']),
    ...mapGetters('app', ['auth']),
    filteredItems() {
      const self = this;

      if (!this.userItems) return [];
      if (this.isDeleteFilter == null) return this.userItems;

      return this.userItems.filter(it => it.isDelete === self.isDeleteFilter);
    },
  },
  mounted() {
    this.fetchData(this.paginationParams).then(() => {
      this.userItems = cloneDeep(this.userList.data);
      this.$forceUpdate();
      this.isInitialized = true;
    });
    console.log('auth', this.auth);
  },
  methods: {
    ...mapActions('userList', { fetchData: 'fetchUserList' }),
    ...mapActions('userList', { deleteUser: 'deleteUser' }),
    onDelete(userToDelete) {
      this.userToDelete = userToDelete;
      this.confirmDeletionMessage =
        `Soll der Benutzer ${this.userToDelete.username} gelöscht werden?`;
      this.isConfirmDialogDisplayed = true;
    },
    onRefuse() {
      this.isConfirmDialogDisplayed = false;
    },
    onConfirm() {
      const self = this;
      this.deleteUser(this.userToDelete)
        .then(() => {
          self.fetchData();
          self.isConfirmDialogDisplayed = false;
        });
    },
    onSubmit() {
      this.refresh();
    },
    refresh() {
      this.fetchData(this.paginationParams).then(() => {
        this.userItems = cloneDeep(this.userList.data);
        this.$forceUpdate();
      });
    },
    onSearch() {
      this.paginationParams.searchTerm = this.searchTerm;
      this.fetchData(this.paginationParams).then(() => {
        this.userItems = cloneDeep(this.userList.data);
        this.$forceUpdate();
      });
    },
  },
};
</script>

<template>
  <div class="user-list">
    <template v-if="userList.hasError">
      <p class="js-error-template ma-3">Failed to fetch user list. Please try again!</p>
    </template>
    <template v-else>
      <v-toolbar dark dense height="40" color="blue-grey lighten-2">
        <v-tooltip bottom>
          <v-btn icon slot="activator" @click="showNewUserDialog = true">
            <v-icon>add</v-icon>
          </v-btn>
          <span>Hinzufügen</span>
        </v-tooltip>

        <v-tooltip bottom>
          <v-btn @click="refresh" icon slot="activator">
            <v-icon>refresh</v-icon>
          </v-btn>
          <span>Aktualisieren</span>
        </v-tooltip>

        <v-spacer />
        <v-flex md3 >
          <v-text-field
            v-model="searchTerm"
            label="Suche"
            height="22"
            class="caption"
            @keyup.enter="onSearch"
            @blur="onSearch"
          />
        </v-flex>
      </v-toolbar>

      <template v-slot:extension>
        <v-flex xs4 md2>
          <v-select
            style="padding-left: 22px; padding-top: 25px;"
            :items="isDeleteOptions"
            v-model="isDeleteFilter"
            item-value="id"
            item-text="text"
            label="Gelöscht" />
        </v-flex>
      </template>

      <v-progress-linear
        v-if="userList.isLoading && !isInitialized"
        :indeterminate="true"
        class="js-loading ma-0"
      />

      <v-data-table
        v-else
        :headers="headers"
        :items="filteredItems"
        :disable-initial-sort="true"
        :pagination="pagination"
        :loading="userList.isLoading"
        :rows-per-page-items="rowsPerPageItems"
        :total-items="this.pagination.totalItems"
        :expand="true"
        item-key="id"
        @update:pagination="loadNextPage"
        rows-per-page-text="Zeilen pro Seite"
        no-data-text="keine Daten verfügbar"
        no-results-text="keine Einträge gefunden"
        class="elevation-1 js-table"
      >
        <template slot="headers" slot-scope="props">
          <tr>
            <th
              v-for="header in props.headers"
              :key="header.text"
              :class="['column sortable',
                       pagination.descending ? 'desc' : 'asc',
                       header.value === pagination.sortBy ? 'active' : '']"
              @click="changeSort(header.value, filteredItems)"
            >
              <v-icon small>arrow_upward</v-icon>
              {{ header.text }}
            </th>
          </tr>
        </template>

        <template slot="items" slot-scope="props">
          <user-list-item :user="props.item" :expanded="props.expanded" @delete="onDelete" />
        </template>
        <template slot="pageText" slot-scope="{ pageStart, pageStop }">
          von {{ pageStart }} bis {{ pageStop }}
        </template>
      </v-data-table>

      <rbx-confirm-dialog @confirm="onConfirm" @refuse="onRefuse"
                          :message="confirmDeletionMessage"
                          :is-displayed="isConfirmDialogDisplayed" />
    </template>

    <new-user-dialog
      v-if="showNewUserDialog"
      @cancel="showNewUserDialog = false"
      @submit="onSubmit"
    />

  </div>
</template>

<style lang="scss" scoped>
.variant-cell {
  width: 30px;

  i {
    cursor: pointer;
  }
}
</style>
